import React from 'react';
import { useQuery } from '@apollo/client/react';
import {
    EquipmentInfoType,
    GetMobDetails,
    GetMobDetails_getMobDetails as MobDetailsResponseType,
    GetMobDetails_getMobDetails_stats as MobDetailsType,
} from '../../../../types/schemaTypes';
import { GET_MOB_DETAILS } from '../../../../graphql/client/queries';
import { Loading, Error } from '../../../_common';
import { blockInvalidChar } from '../../../_common/blockInvalidChar';
import { OverlayTrigger } from 'react-bootstrap';
import moment from 'moment';

interface MobilizationProps {
    jobId: number;
    isEditMode: boolean;
    origin: string;
    originType: string;
    originId: number;
    equipmentsToUpdate: EquipmentInfoType[];
    setEquipmentsToUpdate: React.Dispatch<React.SetStateAction<EquipmentInfoType[]>>;
    mobDetailsFromSignalr: MobDetailsResponseType | undefined;
    mutationLoading: boolean;
    handleOnClickEditLeapFrog: (isLeapFrog: boolean) => void;
}

const Mobilization = ({
    jobId,
    isEditMode,
    origin,
    originType,
    originId,
    equipmentsToUpdate,
    setEquipmentsToUpdate,
    mobDetailsFromSignalr,
    mutationLoading,
    handleOnClickEditLeapFrog,
}: MobilizationProps): React.ReactElement => {
    const { data, loading, error, previousData } = useQuery<GetMobDetails>(GET_MOB_DETAILS, {
        variables: {
            input: {
                originType: originType ?? '-1',
                originId: originId ?? -1,
                destinationId: jobId,
            }
        },
        fetchPolicy: 'network-only',
    });

    let mobDetails: MobDetailsType[] = [];
    let mobStatus = '';
    let leapFrog = false;
    let mobDate = '';
    let mobEndDate = '';
    if (data && data.getMobDetails?.stats) {
        console.log('Mob - Fetched API data')
        data.getMobDetails.stats.map(mobDetail => {
            if (mobDetail)
                mobDetails.push(mobDetail);
        })
        mobStatus = data.getMobDetails.jobInfo?.mobStatus ?? '';
        leapFrog = data.getMobDetails.jobInfo?.leapFrog ?? false;
        mobDate = data.getMobDetails.jobInfo?.mobDate ?? '';
        mobEndDate = data.getMobDetails.jobInfo?.mobEndDate ?? '';
    } else if (previousData && previousData.getMobDetails?.stats) {
        previousData.getMobDetails.stats.map(mobDetail => {
            if (mobDetail)
                mobDetails.push(mobDetail);
        })
        mobStatus = previousData.getMobDetails.jobInfo?.mobStatus ?? '';
        leapFrog = previousData.getMobDetails.jobInfo?.leapFrog ?? false;
        mobDate = previousData.getMobDetails.jobInfo?.mobDate ?? '';
        mobEndDate = previousData.getMobDetails.jobInfo?.mobEndDate ?? '';
    }

    if (isEditMode && mobDetails && equipmentsToUpdate.length === 0) {
        const tempEquipmentsToUpdate: EquipmentInfoType[] = [...equipmentsToUpdate];
        mobDetails.map(equipment => {
            if (equipment) {
                tempEquipmentsToUpdate.push({
                    assetTypeId: equipment.assetTypeId,
                    plannedQty: equipment.plannedQty,
                })
            }
        })
        setEquipmentsToUpdate(tempEquipmentsToUpdate);
    }

    const handleOnChangeInput = (value: number, assetTypeId: number | null) => {
        if (value < 0) return;
        const tempEquipmentsToUpdate = [...equipmentsToUpdate];
        tempEquipmentsToUpdate.map(equipment => {
            if (equipment.assetTypeId === assetTypeId)
                equipment.plannedQty = value;
        })
        setEquipmentsToUpdate(tempEquipmentsToUpdate);
    }

    if (mobDetailsFromSignalr && mobDetailsFromSignalr.stats) {
        mobDetails = [];
        mobDetailsFromSignalr.stats.map(mobDetail => {
            if (mobDetail)
                mobDetails.push(mobDetail);
        })
        mobStatus = mobDetailsFromSignalr.jobInfo?.mobStatus ?? '';
        leapFrog = mobDetailsFromSignalr.jobInfo?.leapFrog ?? false;
        mobDate = mobDetailsFromSignalr.jobInfo?.mobDate ?? '';
        mobEndDate = mobDetailsFromSignalr.jobInfo?.mobEndDate ?? '';
    }

    const formatDate = (text: string | null): string => {
        if (text === null || text === '') return '';
        const newDate = moment(text).format('MMM DD, YYYY');
        return newDate;
    };

    return (
        <div className="Equipments-Container">
            {(loading || mutationLoading) && (
                <div className="Equipments-Spinner">
                    <Loading />
                </div>
            )}
            {
                error ? (
                    <Error error={error} />
                ) : (
                    <div className="Equipments-MobDemob-Container">
                        {!loading &&
                            <div>
                                <div className="Equipments-MobDemob-JobDetails-Container">
                                    {mobStatus !== "" && (
                                        <div className="Equipments-MobStatus-Container">
                                            <i className="material-icons Equipments-MobStatus-Icon">
                                                cached
                                            </i>
                                            {mobStatus}
                                        </div>
                                    )}

                                    <div className="Equipments-MobDate-Container">
                                        Start: <span className="Equipments-MobDate">{formatDate(mobDate)}</span>
                                    </div>

                                    <div className="Equipments-MobDate-Container">
                                        End: <span className="Equipments-MobDate">{formatDate(mobEndDate)}</span>
                                    </div>
                                </div>
                                <label className="Equipments-Leapfrog-Container">
                                    <input
                                        type='checkbox'
                                        className='Equipments-Leapfrog'
                                        id='EditJob-Leapfrog-Checkbox'
                                        checked={leapFrog}
                                        onChange={event => { handleOnClickEditLeapFrog(event.target.checked) }}
                                    />
                                    LEAPFROG
                                </label>
                            </div>
                        }

                        <table className="Equipments-MobDemob-Grid" cellPadding={2} cellSpacing={2}>
                            <thead className="Equipments-MobDemob-Grid-Header">
                                <tr>
                                    <th className="Equipments-MobDemob-Grid-Head Equipments-MobDemob-Data-LeftAlign">
                                        Asset Name
                                        <OverlayTrigger
                                            placement="top"
                                            delay={{ show: 250, hide: 400 }}
                                            overlay={
                                                <div className="Equipments-MobDemob-Tooltip">
                                                    Data reflected in this grid indicates the individual count of assets
                                                </div>
                                            }
                                        >
                                            <i className="material-icons color Equipments-Info-Icon">info</i>
                                        </OverlayTrigger>
                                    </th>
                                    <th>Planned Quantity</th>
                                    <th>Available for Mobilization</th>
                                    <th className="Equipments-MobDemob-Grid-Head">
                                        Additional Needed
                                        <OverlayTrigger
                                            placement="top"
                                            delay={{ show: 250, hide: 400 }}
                                            overlay={
                                                <div className="Equipments-MobDemob-Tooltip">
                                                    Count of assets still needed to meet planned quantity at {origin}
                                                </div>
                                            }
                                        >
                                            <i className="material-icons color Equipments-Info-Icon">info</i>
                                        </OverlayTrigger>
                                    </th>
                                    <th>Created</th>
                                    <th>Ready for Dispatch</th>
                                    <th>In-Route</th>
                                    <th>Delivered/ Mobilized</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    mobDetails.map((mobDetail, index) => {
                                        if (isEditMode)
                                            return (
                                                <tr key={index}>
                                                    <td className="Equipments-MobDemob-Data-LeftAlign">{mobDetail?.assetType}</td>
                                                    <td>
                                                        <input
                                                            type='number'
                                                            className='Equipments-MobDemob-Input'
                                                            value={equipmentsToUpdate ? (equipmentsToUpdate[index]?.plannedQty ?? '') : ''}
                                                            onChange={event => handleOnChangeInput(parseInt(event.target.value), mobDetail.assetTypeId)}
                                                            onKeyDown={blockInvalidChar}
                                                        />
                                                    </td>
                                                    <td>{mobDetail?.availableQty && mobDetail?.availableQty !== 0 ? mobDetail.availableQty : '-'}</td>
                                                    <td>{mobDetail?.additionalNeeded && mobDetail?.additionalNeeded !== 0 ? mobDetail.additionalNeeded : '-'}</td>
                                                    <td>{mobDetail?.created && mobDetail?.created !== 0 ? mobDetail.created : '-'}</td>
                                                    <td>{mobDetail?.readyForDispatch && mobDetail?.readyForDispatch !== 0 ? mobDetail.readyForDispatch : '-'}</td>
                                                    <td>{mobDetail?.inRoute && mobDetail?.inRoute !== 0 ? mobDetail.inRoute : '-'}</td>
                                                    <td>{mobDetail?.delivered && mobDetail?.delivered !== 0 ? mobDetail.delivered : '-'}</td>
                                                </tr>
                                            )
                                        else if (
                                            mobDetail.plannedQty ||
                                            mobDetail.availableQty ||
                                            mobDetail.additionalNeeded ||
                                            mobDetail.created ||
                                            mobDetail.readyForDispatch ||
                                            mobDetail.inRoute ||
                                            mobDetail.delivered
                                        )
                                            return (
                                                <tr key={index}>
                                                    <td className="Equipments-MobDemob-Data-LeftAlign">{mobDetail?.assetType}</td>
                                                    <td>{mobDetail?.plannedQty && mobDetail?.plannedQty !== 0 ? mobDetail.plannedQty : '-'}</td>
                                                    <td>{mobDetail?.availableQty && mobDetail?.availableQty !== 0 ? mobDetail.availableQty : '-'}</td>
                                                    <td>{mobDetail?.additionalNeeded && mobDetail?.additionalNeeded !== 0 ? mobDetail.additionalNeeded : '-'}</td>
                                                    <td>{mobDetail?.created && mobDetail?.created !== 0 ? mobDetail.created : '-'}</td>
                                                    <td>{mobDetail?.readyForDispatch && mobDetail?.readyForDispatch !== 0 ? mobDetail.readyForDispatch : '-'}</td>
                                                    <td>{mobDetail?.inRoute && mobDetail?.inRoute !== 0 ? mobDetail.inRoute : '-'}</td>
                                                    <td>{mobDetail?.delivered && mobDetail?.delivered !== 0 ? mobDetail.delivered : '-'}</td>
                                                </tr>
                                            )
                                    })
                                }
                            </tbody>
                        </table>
                    </div>
                )
            }
        </div>
    );
};

export default Mobilization;
